import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  selectAdvisorOnboardingCompletedStepsData,
  selectSession,
  selectSessionAdvisor,
  selectShouldDisallowUsage,
} from '../model/selectors';

import { AdvisorAccountSponsorship } from '@client/pages/AdvisorAccountSponsorship';
import { AdvisorAccountSeats } from '@client/pages/AdvisorAccountSeats';
import { AdvisorCheckoutPage } from '@client/pages/AdvisorCheckoutPage';
import { AdvisorIntegrations } from '@client/pages/AdvisorIntegrations';
import { AdvisorUsageAnalytics } from '@client/pages/AdvisorUsageAnalytics';
import { AdvisorHierarchyTeamPage } from '@client/pages/AdvisorHierarchyTeamPage';
import { WizardAdvisorAdminPortal } from '@client/pages/WizardAdvisorAdminPortal';
import { SessionType } from '@shared';
import { Menu } from 'lucide-react';
import { AdvisorDashboardClients } from '../pages/AdvisorDashboardClients';
import { AdvisorDashboardSettings } from '../pages/AdvisorDashboardSettings';
import { useAdvisorNavigation } from '../hooks/useAdvisorNavigation';
import { ONBOARDING_STEPS } from '@client/hooks/useAdvisorOnboarding';
import {
  AdvisorDashboardSideBar,
  AdvisorDashboardSideBarContent,
  AdvisorDashboardSideBarMobile,
} from './AdvisorDashboardSideBar';
import { useSignOut } from './hooks/use-sign-out';
import { DEFAULT_DASHBOARD_CONFIG, type DashboardConfig } from './dashboard-config';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { Button } from './ui/button';
import { InviteClientDialog } from '@client/features/invite-client';
import { SendReminderProvider, SendReminderModal } from '@client/features/send-reminder';

import { UnauthorizedPage } from './UnauthorizedPage';
import { AdvisorMetricsPage } from '@client/features/wizard-advisors-metrics';

export type DashboardActivePage =
  | 'clients'
  | 'settings'
  | 'sponsorship'
  | 'seats'
  | 'team'
  | 'usage'
  | 'integrations'
  | 'admin-portal'
  | 'calculation-tests'
  | 'metrics' /* | 'opportunities' */;

// Custom hooks for AdvisorDashboardShell
const useActivePage = () => {
  const location = useLocation();

  const getActivePageFromPath = (): DashboardActivePage => {
    const path = location.pathname;
    const normalizedPath = path.replace(/\/advisors\/[^/]+/, '/advisors');

    if (path.startsWith('/wizard/calculation-snapshots')) return 'calculation-tests';
    if (normalizedPath.includes('/settings')) return 'settings';
    // else if (normalizedPath.includes('/opportunities')) return 'opportunities';
    else if (normalizedPath.includes('/seats')) return 'seats';
    else if (normalizedPath.includes('/sponsorship')) return 'sponsorship';
    else if (normalizedPath.includes('/team')) return 'team';
    else if (normalizedPath.includes('/integrations')) return 'integrations';
    else if (normalizedPath.includes('/usage')) return 'usage';
    else if (normalizedPath.includes('/admin-portal')) return 'admin-portal';
    else if (normalizedPath.includes('/metrics')) return 'metrics';
    else return 'clients';
  };

  const [activePage, setActivePage] = useState<DashboardActivePage>(getActivePageFromPath());

  useEffect(() => {
    const newActivePage = getActivePageFromPath();
    setActivePage(newActivePage);
  }, [location.pathname]);

  return { activePage, getActivePageFromPath };
};

const useUnsavedSettings = () => {
  const [hasUnsavedSettings, setHasUnsavedSettings] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasUnsavedSettings && pendingNavigationPath) {
      navigate(pendingNavigationPath);
      setPendingNavigationPath(null);
    }
  }, [hasUnsavedSettings, pendingNavigationPath, navigate]);

  return {
    hasUnsavedSettings,
    setHasUnsavedSettings,
    showUnsavedDialog,
    setShowUnsavedDialog,
    pendingNavigationPath,
    setPendingNavigationPath,
  };
};

const useSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return { sidebarOpen, setSidebarOpen };
};

interface AdvisorDashboardShellProps {
  config?: DashboardConfig;
  content?: ReactNode;
}

export default function AdvisorDashboardShell({
  config = DEFAULT_DASHBOARD_CONFIG,
  content,
}: AdvisorDashboardShellProps = {}) {
  const advisor = useSelector(selectSessionAdvisor);
  const { sessionType } = useSelector(selectSession);
  const shouldDisallowUsage = useSelector(selectShouldDisallowUsage);
  const { flags } = useFeatureFlags();
  const onboardingCompletedSteps = useSelector(selectAdvisorOnboardingCompletedStepsData);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const hasCompletedAllOnboardingSteps =
    onboardingCompletedSteps?.completedSteps &&
    ONBOARDING_STEPS.every(step => onboardingCompletedSteps.completedSteps.includes(step));
  const hasIncompleteOnboarding = !hasCompletedAllOnboardingSteps;

  const { activePage } = useActivePage();
  const handleClickSignOut = useSignOut();
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const { hasUnsavedSettings, setHasUnsavedSettings, showUnsavedDialog, setShowUnsavedDialog } =
    useUnsavedSettings();

  const {
    navigateToClientsDashboard,
    navigateToAdvisorProfile,
    // navigateToOpportunitiesDashboard,
    navigateToSponsorshipDashboard,
    navigateToOnboarding,
    navigateToSeatsDashboard,
    navigateToIntegrations,
    navigateToTeamDashboard,
    navigateToUsageDashboard,
    navigateToAdminPortal,
    navigateToMetrics,
  } = useAdvisorNavigation();

  const handleOnPageChange = useCallback(
    (page: DashboardActivePage) => {
      setSidebarOpen(false);
      if (hasUnsavedSettings) {
        setShowUnsavedDialog(true);
        return;
      }

      if (page === 'clients') {
        navigateToClientsDashboard();
      } else if (page === 'settings') {
        navigateToAdvisorProfile();
      } else if (page === 'seats') {
        navigateToSeatsDashboard();
      } else if (page === 'sponsorship') {
        navigateToSponsorshipDashboard();
      } else if (page === 'team') {
        navigateToTeamDashboard();
      } else if (page === 'integrations') {
        navigateToIntegrations();
      } else if (page === 'usage') {
        navigateToUsageDashboard();
      } else if (page === 'admin-portal') {
        navigateToAdminPortal();
      } else if (page === 'metrics') {
        navigateToMetrics();
      } /* else if (page === 'opportunities') {
        navigateToOpportunitiesDashboard();
      } */
    },
    [
      setSidebarOpen,
      hasUnsavedSettings,
      setShowUnsavedDialog,
      navigateToClientsDashboard,
      navigateToAdvisorProfile,
      // navigateToOpportunitiesDashboard,
      navigateToSponsorshipDashboard,
      navigateToSeatsDashboard,
      navigateToIntegrations,
      navigateToTeamDashboard,
      navigateToUsageDashboard,
      navigateToAdminPortal,
      navigateToMetrics,
    ],
  );

  const renderAdvisorSideBarMobile = useCallback(
    () => (
      <AdvisorDashboardSideBarMobile
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      >
        <AdvisorDashboardSideBarContent
          activePage={activePage}
          onPageChange={handleOnPageChange}
          onNavigateToOnboarding={navigateToOnboarding}
          hasIncompleteOnboarding={hasIncompleteOnboarding}
          isDisabled={shouldDisallowUsage}
          handleClickSignOut={handleClickSignOut}
          sidebarConfig={config.sidebar}
        />
      </AdvisorDashboardSideBarMobile>
    ),
    [
      sidebarOpen,
      setSidebarOpen,
      activePage,
      handleOnPageChange,
      shouldDisallowUsage,
      handleClickSignOut,
      config.sidebar,
    ],
  );

  const renderSideBarMobileButton = useCallback(
    () => (
      <button
        type="button"
        className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Menu
          className="h-7 w-7 rotate-180 rounded-full bg-gray-100 p-1 text-gray-800"
          aria-hidden="true"
        />
      </button>
    ),
    [setSidebarOpen],
  );

  return (
    <SendReminderProvider>
      <div className="flex h-screen">
        <AdvisorDashboardSideBar
          advisorSideBarMobile={renderAdvisorSideBarMobile()}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
        >
          <AdvisorDashboardSideBarContent
            key={`mobile-sidebar-${activePage}`}
            activePage={activePage}
            onPageChange={handleOnPageChange}
            onNavigateToOnboarding={navigateToOnboarding}
            hasIncompleteOnboarding={hasIncompleteOnboarding}
            isDisabled={shouldDisallowUsage}
            handleClickSignOut={handleClickSignOut}
            sidebarConfig={config.sidebar}
          />
        </AdvisorDashboardSideBar>
        <div className="flex flex-1 flex-col overflow-y-auto">
          <div className="sticky top-0 z-50 flex items-center gap-2 p-4 lg:hidden">
            {renderSideBarMobileButton()}
          </div>
          {isSidebarCollapsed && (
            <Button
              variant="outline"
              size="icon"
              className="border-primary-300 text-primary-dark hover:text-primary-hover absolute left-2 top-2 z-50 hidden h-7 w-7 lg:flex"
              id="open-sidebar"
              data-testid="open-sidebar"
              onClick={() => setIsSidebarCollapsed(false)}
            >
              <span className="sr-only">Open sidebar</span>
              <Menu className="h-4 w-4" />
            </Button>
          )}
          <main className="relative flex-1">
            {content ?? (
              <Routes>
              <Route
                path="/clients"
                element={<AdvisorDashboardClients config={config.clients} />}
              />

              <Route
                path="/settings"
                element={
                  <AdvisorDashboardSettings
                    hasUnsavedChanges={hasUnsavedSettings}
                    setHasUnsavedChanges={setHasUnsavedSettings}
                    showUnsavedDialog={showUnsavedDialog}
                    setShowUnsavedDialog={setShowUnsavedDialog}
                  />
                }
              />

              <Route
                path="/sponsorship"
                element={<AdvisorAccountSponsorship />}
              />

              <Route
                path="/seats"
                element={<AdvisorAccountSeats />}
              />

              <Route
                path="/team"
                element={<AdvisorHierarchyTeamPage />}
              />

              <Route
                path="/integrations/*"
                element={<AdvisorIntegrations />}
              />
              <Route
                path="/usage"
                element={<AdvisorUsageAnalytics />}
              />

              <Route
                path="/admin-portal"
                element={
                  sessionType === SessionType.wizard ? (
                    <WizardAdvisorAdminPortal />
                  ) : (
                    <UnauthorizedPage />
                  )
                }
              />
              <Route
                path="/metrics"
                element={
                  sessionType === SessionType.wizard ? <AdvisorMetricsPage /> : <UnauthorizedPage />
                }
              />
              <Route
                path="/checkout"
                element={<AdvisorCheckoutPage />}
              />
              </Routes>
            )}
          </main>
        </div>
        {(flags.inviteClientDialog ?? true) && (
          <InviteClientDialog
            title="Invite a client"
            description="Send an invitation to help your clients plan for long-term care"
          />
        )}
        <SendReminderModal />
      </div>
    </SendReminderProvider>
  );
}
